import { lazy, Suspense, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import { useTranslation } from "react-i18next";
import { useDriveView } from "./driveViewStore";
import DriveLoading from "./DriveLoading";
import DriveFeedback from "./DriveFeedback";
import { useVehiclesSocketStore } from "../../store/admin/vehiclesSocketStore";

// MapLibre pesa ~800 KB: sólo se descarga cuando alguien abre la vista.
const DriveView = lazy(() => import("./DriveView"));

// Mismo corte que LiteLayout (MOBILE_BREAKPOINT = 768): en móvil no cabe una
// ventanita sobre el mapa, así que la vista ocupa el mapa completo.
const MOBILE_QUERY = "(max-width: 767px)";

function useIsMobile() {
  const [mobile, setMobile] = useState(() => window.matchMedia(MOBILE_QUERY).matches);
  useEffect(() => {
    const mq = window.matchMedia(MOBILE_QUERY);
    const on = () => setMobile(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return mobile;
}

// Dónde la dejó el usuario (distancia a la esquina inferior derecha del mapa).
// Por navegador: es comodidad de quien mira. El default deja libre el zoom de
// Google, que ocupa ≈40 px abajo a la derecha.
type Pos = { right: number; bottom: number };
const POS_KEY = "driveView.pos";
const DEFAULT_POS: Pos = { right: 64, bottom: 24 };
const readPos = (): Pos => {
  try {
    const p = JSON.parse(localStorage.getItem(POS_KEY) || "null");
    return p && Number.isFinite(p.right) && Number.isFinite(p.bottom) ? p : DEFAULT_POS;
  } catch {
    return DEFAULT_POS;
  }
};
const writePos = (p: Pos) => {
  try {
    localStorage.setItem(POS_KEY, JSON.stringify(p));
  } catch {
    /* sin almacenamiento: vale sólo para esta sesión */
  }
};

/**
 * Vista de manejo (beta) flotando sobre el mapa de LiteLayout: ventanita 9:16
 * que se arrastra desde su barra, se minimiza a una pastilla y se cierra.
 * Daniel probó acoplarla a un costado (el mapa se angostaba) y prefirió no
 * quitarle espacio al mapa. Expandida (o en móvil) cubre el mapa completo.
 */
export default function DriveViewHost({ isDarkMode }: { isDarkMode: boolean }) {
  const { t } = useTranslation("lite");
  const { vehicleId, expanded, dismiss, setExpanded, follow } = useDriveView();
  const close = useDriveView((s) => s.close);
  const asking = useDriveView((s) => s.asking);
  const isMobile = useIsMobile();
  const [minimized, setMinimized] = useState(false);
  const [pos, setPos] = useState<Pos>(readPos);
  const boxRef = useRef<HTMLDivElement>(null);
  const drag = useRef<{ x: number; y: number; pos: Pos; now: Pos } | null>(null);
  const full = (expanded || isMobile) && !minimized;

  const focusedId = useVehiclesSocketStore((s) => s.focusedVehicleId);
  const company = useVehiclesSocketStore((s) => s.selectedCompany);
  const present = useVehiclesSocketStore((s) => (vehicleId ? s.vehiclesMap.has(vehicleId) : true));
  // El modelo elegido en el garaje: si cambia, la vista se monta de nuevo con él.
  const pointerModel = useVehiclesSocketStore((s) => {
    const i = vehicleId ? s.vehiclesMap.get(vehicleId) : undefined;
    return i === undefined ? "" : s.vehicles[i]?.vehicle?.pointerModel ?? "";
  });
  // ¿Ya tiene posición? Sin ella la vista muestra «aún no tiene posición» y no se
  // rearmaba sola al llegar el primer reporte: había que cerrarla y abrirla. Va en
  // el key para montarla de nuevo en cuanto la tenga. Mismo criterio que lngLatOf
  // de DriveView (no se importa: DriveView es lazy y arrastraría maplibre).
  const hasPosition = useVehiclesSocketStore((s) => {
    const i = vehicleId ? s.vehiclesMap.get(vehicleId) : undefined;
    const v = i === undefined ? undefined : s.vehicles[i];
    return !!v && Number.isFinite(v.longitude) && Number.isFinite(v.latitude) && (v.longitude !== 0 || v.latitude !== 0);
  });
  const speed = useVehiclesSocketStore((s) => {
    const i = vehicleId ? s.vehiclesMap.get(vehicleId) : undefined;
    return i === undefined ? 0 : Math.round(s.vehicles[i]?.speed ?? 0);
  });

  // Seleccionar una unidad la abre sola, en la lista o en el mapa.
  useEffect(() => {
    // En móvil sólo cuenta deseleccionar (cerrar): abrir es con el botón del mapa,
    // porque ahí ocupa el mapa completo y taparía cada toque.
    if (!isMobile || !focusedId) follow(focusedId);
  }, [focusedId, isMobile, follow]);

  // Que no quede abierta una unidad que ya no se ve: otra compañía (admin
  // reseller) o la unidad salió de la lista.
  const lastCompany = useRef(company);
  useEffect(() => {
    if (lastCompany.current === company) return; // al montar no: cerraría la recién abierta
    lastCompany.current = company;
    close();
  }, [company, close]);
  useEffect(() => {
    if (!present) close();
  }, [present, close]);
  // Al cerrar, la próxima vez abre normal (no minimizada).
  useEffect(() => {
    if (!vehicleId) setMinimized(false);
  }, [vehicleId]);

  // Esc: de pantalla completa vuelve a la ventanita; de la ventanita, cierra.
  useEffect(() => {
    if (!vehicleId) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (expanded && !isMobile) setExpanded(false);
      else dismiss();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [vehicleId, expanded, isMobile, dismiss, setExpanded]);

  // Cerrada: si toca, la pregunta de la beta (modal centrado).
  if (!vehicleId) {
    return asking ? <DriveFeedback isDarkMode={isDarkMode} /> : null;
  }

  // Arrastre desde la barra: se mueve el estilo directo (sin re-render por
  // cuadro) y se guarda al soltar. Se limita al área del mapa.
  const clamp = (p: Pos): Pos => {
    const box = boxRef.current;
    const area = box?.offsetParent as HTMLElement | null;
    if (!box || !area) return p;
    const maxRight = Math.max(0, area.clientWidth - box.offsetWidth);
    const maxBottom = Math.max(0, area.clientHeight - box.offsetHeight);
    return { right: Math.min(maxRight, Math.max(0, p.right)), bottom: Math.min(maxBottom, Math.max(0, p.bottom)) };
  };
  const onDragStart = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (full || (e.target as HTMLElement).closest("button")) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { x: e.clientX, y: e.clientY, pos, now: pos };
  };
  const onDragMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    const box = boxRef.current;
    if (!d || !box) return;
    d.now = clamp({ right: d.pos.right - (e.clientX - d.x), bottom: d.pos.bottom - (e.clientY - d.y) });
    box.style.right = `${d.now.right}px`;
    box.style.bottom = `${d.now.bottom}px`;
  };
  const onDragEnd = () => {
    const d = drag.current;
    if (!d) return;
    drag.current = null;
    setPos(d.now);
    writePos(d.now);
  };
  const dragProps = { onPointerDown: onDragStart, onPointerMove: onDragMove, onPointerUp: onDragEnd, onPointerCancel: onDragEnd };

  const btn = "h-6 w-6 flex items-center justify-center rounded-md text-white/80 transition-[background-color,transform] duration-150 hover:bg-white/15 hover:text-white active:scale-[0.97] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500";
  const expandLabel = expanded ? t("driveView.collapse", "Volver a la ventanita") : t("driveView.expand", "Ver en todo el mapa");
  // En móvil la vista tapa el mapa: el ✕ se lee como «volver al mapa».
  const closeLabel = isMobile ? t("driveView.backToMap", "Volver al mapa") : t("driveView.close", "Cerrar");
  const closeIcon = isMobile ? "ri-map-2-line" : "ri-close-line";
  const beta = (
    <span className="rounded bg-primary-500 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-gray-900">
      {t("driveView.beta", "Beta")}
    </span>
  );
  const anchor = full ? undefined : { right: pos.right, bottom: pos.bottom };

  // Minimizada: sólo una pastilla con la velocidad. El mapa 3D se desmonta
  // (no gasta GPU ni batería) y al restaurar vuelve a cargar en 1-2 s.
  if (minimized) {
    return (
      <div
        ref={boxRef}
        className="absolute z-40 flex select-none items-center gap-2 rounded-full bg-black/75 py-1 pl-3 pr-1 text-white shadow-xl ring-1 ring-white/10 backdrop-blur-md touch-none cursor-grab active:cursor-grabbing"
        style={anchor}
        {...dragProps}
      >
        <i className="ri-gamepad-line text-primary-500" aria-hidden />
        <span className="font-mono text-xs tabular-nums">{speed} km/h</span>
        {beta}
        <button type="button" className={btn} onClick={() => setMinimized(false)} aria-label={t("driveView.restore", "Abrir vista de manejo")} title={t("driveView.restore", "Abrir vista de manejo")}>
          <i className="ri-picture-in-picture-exit-line" aria-hidden />
        </button>
        <button type="button" className={btn} onClick={dismiss} aria-label={closeLabel} title={closeLabel}>
          <i className="ri-close-line" aria-hidden />
        </button>
      </div>
    );
  }

  return (
    <div
      ref={boxRef}
      // z-40: arriba de la tarjeta de la unidad (z-30) y del globito (z-20), pero
      // debajo de los menús del encabezado y del panel (z-50). Con z-60 el menú del
      // usuario salía debajo de la vista (Daniel, 29-oct).
      className={`absolute z-40 overflow-hidden ${full ? "inset-0" : "aspect-[9/16] rounded-2xl shadow-2xl ring-1 ring-black/20"}`}
      // Alto = el menor entre 390 px y 60 % del mapa; el ancho sale del 9:16. En una
      // laptop con el mapa de ~550 px de alto, 390 px dejaban la ventanita cortada.
      style={full ? undefined : { ...anchor, height: "min(390px, 60%)" }}
    >
      <Suspense fallback={<DriveLoading />}>
        {/* key: cambiar de unidad o de vehículo en el garaje monta la vista de nuevo
            (con «Cargando») en su posición y con su imagen, sin arrastrar la anterior. */}
        <DriveView key={`${vehicleId}:${pointerModel}:${hasPosition}`} vehicleId={vehicleId} isDarkMode={isDarkMode} full={full} />
      </Suspense>
      {/* Barra: se arrastra desde aquí y lleva los controles, para no tapar la escena. */}
      <div
        className={`absolute inset-x-0 top-0 z-20 flex h-8 select-none items-center gap-1.5 bg-gradient-to-b from-black/60 to-transparent pl-2 pr-1 text-white touch-none ${full ? "" : "cursor-grab active:cursor-grabbing"}`}
        {...dragProps}
      >
        {!full && <i className="ri-draggable text-white/50" aria-hidden />}
        {beta}
        <div className="ml-auto flex items-center">
          <button type="button" className={btn} onClick={() => setMinimized(true)} aria-label={t("driveView.minimize", "Minimizar")} title={t("driveView.minimize", "Minimizar")}>
            <i className="ri-subtract-line" aria-hidden />
          </button>
          {!isMobile && (
            <button type="button" className={btn} onClick={() => setExpanded(!expanded)} aria-label={expandLabel} title={expandLabel}>
              <i className={expanded ? "ri-collapse-diagonal-line" : "ri-expand-diagonal-line"} aria-hidden />
            </button>
          )}
          <button type="button" className={btn} onClick={dismiss} aria-label={closeLabel} title={closeLabel}>
            <i className={closeIcon} aria-hidden />
          </button>
        </div>
      </div>
    </div>
  );
}
